import React, { useState, useEffect } from "react";
import { useSocialCoachSession, getErrorMessage, googleDuplicateWarning } from "@/utils";
import {
  CoachSocialNetworkProfileServices,
  BulkAssignAssignment,
  CoachSocialNetworkProfile,
  SocialAccountOption,
  PlayersServices,
  PlayerSearchResult,
} from "@/services/bedrock";
import { SocialNetworkIcon } from "@/components/SocialNetworkIcon";
import { socialNetworkConnectionNames, socialNetworkNames } from "@/interfaces";
import { Button, Dialog, Input, Table, Alert, Spinner, Tooltip } from "@/components/ui";
import { CheckIcon, Trash2, Users, Plus, Info, Search, AlertCircle, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { ImportMappingsDialog } from "./ImportMappingsDialog";

export interface BulkAssignDialogProps {
  profile: CoachSocialNetworkProfile;
  onClose: () => void;
}

export interface StagedAssignment {
  player: PlayerSearchResult;
  connection: SocialAccountOption;
}

export function BulkAssignDialog({ profile, onClose }: BulkAssignDialogProps) {
  const { coachId } = useSocialCoachSession();

  const [loading, setLoading] = useState(true);
  const [loadingPlayersProgress, setLoadingPlayersProgress] = useState({ current: 0, total: 0 });
  const [submitting, setSubmitting] = useState(false);
  const [autoMatching, setAutoMatching] = useState(false);

  // Data
  const [accountOptions, setAccountOptions] = useState<SocialAccountOption[]>([]);
  const [players, setPlayers] = useState<PlayerSearchResult[]>([]);

  // Wizard Step State
  const [step, setStep] = useState<"build" | "review">("build");

  // Selection & Staging states
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null);
  const [stagedAssignments, setStagedAssignments] = useState<StagedAssignment[]>([]);
  const [existingConnections, setExistingConnections] = useState<CoachSocialNetworkProfile[]>([]);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [viewPlayersForOption, setViewPlayersForOption] = useState<{
    option: SocialAccountOption;
    players: { name: string; email: string; isStaged?: boolean }[];
  } | null>(null);

  // Filter queries & checkboxes
  const [playerSearchQuery, setPlayerSearchQuery] = useState("");
  const [optionSearchQuery, setOptionSearchQuery] = useState("");

  // Un-debounced text values to ensure instant, lag-free typing feedback
  const [playerSearchInputValue, setPlayerSearchInputValue] = useState("");
  const [optionSearchInputValue, setOptionSearchInputValue] = useState("");

  // Debounce option search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setOptionSearchQuery(optionSearchInputValue);
    }, 250);

    return () => {
      clearTimeout(handler);
    };
  }, [optionSearchInputValue]);

  // Debounce player search query
  useEffect(() => {
    const handler = setTimeout(() => {
      setPlayerSearchQuery(playerSearchInputValue);
    }, 250);

    return () => {
      clearTimeout(handler);
    };
  }, [playerSearchInputValue]);
  const [selectedSegmentation, setSelectedSegmentation] = useState<string>("");
  const [hideConnectedOptions, setHideConnectedOptions] = useState(true);
  const [filterNeedsConnection, setFilterNeedsConnection] = useState(true);

  // Failure state (for 207 Multi-Status)
  const [partialFailure, setPartialFailure] = useState<{
    warning: string;
    messages: string[];
  } | null>(null);

  // Load all options, paged players, and existing child connection assignments in a single unified sequence
  useEffect(() => {
    if (!coachId) return;

    const loadAllData = async () => {
      setLoading(true);
      setLoadingPlayersProgress({ current: 0, total: 0 });

      try {
        // Step 1: Fetch child options and first page of players in parallel
        const [opts, firstPageResponse] = await Promise.all([
          CoachSocialNetworkProfileServices.getSelectableAccountOptions(coachId, profile.id).catch(e => {
            console.error("Failed to load selectable options:", e);
            toast.error("Failed to load selectable sub-accounts or child options.");
            return [] as SocialAccountOption[];
          }),
          PlayersServices.searchPlayers({ page: 1, perPage: 100 }).catch(e => {
            console.error("Failed to load first page of players:", e);
            toast.error("Failed to load players list.");
            return { content: [] as PlayerSearchResult[], pagination: { totalPages: 1 } };
          }),
        ]);

        const loadedOptions = opts || [];
        setAccountOptions(loadedOptions);

        let allPlayersList = [...(firstPageResponse.content || [])];
        const totalPages = (firstPageResponse.pagination as any)?.totalPages || 1;
        setLoadingPlayersProgress({ current: 1, total: totalPages });

        // Step 2: Fetch remaining pages of players in parallel
        if (totalPages > 1) {
          const promises: Promise<any>[] = [];
          for (let p = 2; p <= totalPages; p++) {
            const fetchPage = async (pageNumber: number) => {
              try {
                const res = await PlayersServices.searchPlayers({ page: pageNumber, perPage: 100 });
                setLoadingPlayersProgress(prev => ({ ...prev, current: prev.current + 1 }));
                return res;
              } catch (e) {
                console.error(`Failed to load page ${pageNumber} of players:`, e);
                // Increment progress on error so the loader doesn't hang visually
                setLoadingPlayersProgress(prev => ({ ...prev, current: prev.current + 1 }));
                return { content: [] };
              }
            };
            promises.push(fetchPage(p));
          }

          const results = await Promise.all(promises);
          results.forEach(res => {
            allPlayersList = [...allPlayersList, ...(res.content || [])];
          });
        }
        setPlayers(allPlayersList);

        // Step 3: Fetch existing connections sequentially to drive filters and displays
        if (loadedOptions.length > 0 && allPlayersList.length > 0) {
          try {
            const activeRes = await CoachSocialNetworkProfileServices.getActiveChildProfiles(
              coachId,
              profile.id,
              1,
              100
            );
            setExistingConnections(activeRes.content || []);
          } catch (e) {
            console.error("Failed to load existing child connections:", e);
          }
        }
      } catch (e) {
        console.error("Critical error inside loadAllData:", e);
      } finally {
        setLoading(false);
      }
    };

    loadAllData();
  }, [coachId, profile.id]);

  // Safe helper getters to support both direct properties and nested playerAccount object
  const getPlayerFirstName = (p: PlayerSearchResult) => p.firstName || (p as any).playerAccount?.firstName || "";
  const getPlayerLastName = (p: PlayerSearchResult) => p.lastName || (p as any).playerAccount?.lastName || "";
  const getPlayerEmail = (p: PlayerSearchResult) => p.email || (p as any).playerAccount?.email || "";
  const getPlayerRegions = (p: PlayerSearchResult): string[] => {
    if (p.segmentationDetails) {
      return p.segmentationDetails.filter(s => s.type === "REGION").map(s => s.name);
    }
    return (p as any).regionNames || (p as any).playerAccount?.regionNames || (p as any).info?.regionNames || [];
  };
  const getPlayerBranches = (p: PlayerSearchResult): string[] => {
    if (p.segmentationDetails) {
      return p.segmentationDetails.filter(s => s.type === "BRANCH").map(s => s.name);
    }
    return (p as any).branchNames || (p as any).playerAccount?.branchNames || (p as any).info?.branchNames || [];
  };

  // Dynamic regions/branches from loaded players
  const uniqueRegions = React.useMemo(() => {
    const regions = new Set<string>();
    players.forEach(p => {
      getPlayerRegions(p).forEach(r => {
        if (r) regions.add(r);
      });
    });
    return Array.from(regions).sort();
  }, [players]);

  const uniqueBranches = React.useMemo(() => {
    const branches = new Set<string>();
    players.forEach(p => {
      getPlayerBranches(p).forEach(b => {
        if (b) branches.add(b);
      });
    });
    return Array.from(branches).sort();
  }, [players]);

  // 1. Set of connection options that are already connected on the server
  const connectedOptionIdsSet = React.useMemo(() => {
    const ids = new Set<string>();
    players.forEach(p => {
      p.activeSocialNetworkProfiles?.forEach(s => {
        if (s.socialNetworkId === profile.socialNetworkId) {
          if (s.socialIdentity?.externalId) ids.add(s.socialIdentity.externalId);
          if (s.socialIdentity?.id) ids.add(s.socialIdentity.id);
        }
      });
    });
    return ids;
  }, [players, profile.socialNetworkId]);

  // 2. Set of connection options that are currently staged in this session
  const stagedConnectionIdsSet = React.useMemo(() => {
    return new Set(stagedAssignments.map(s => s.connection.id));
  }, [stagedAssignments]);

  // 3. Set of players who are staged in this session
  const stagedPlayerIdsSet = React.useMemo(() => {
    return new Set(stagedAssignments.map(s => s.player.id));
  }, [stagedAssignments]);

  // 4. Map of connection option ID to a list of players currently connected to it on the server
  const optionToPlayersMap = React.useMemo(() => {
    const map = new Map<string, PlayerSearchResult[]>();
    players.forEach(p => {
      p.activeSocialNetworkProfiles?.forEach(s => {
        if (s.socialNetworkId === profile.socialNetworkId) {
          const extId = s.socialIdentity?.externalId;
          const id = s.socialIdentity?.id;
          if (extId) {
            const list = map.get(extId) || [];
            list.push(p);
            map.set(extId, list);
          }
          if (id && id !== extId) {
            const list = map.get(id) || [];
            list.push(p);
            map.set(id, list);
          }
        }
      });
    });
    return map;
  }, [players, profile.socialNetworkId]);

  // 5. Map of connection option ID to the staged assignment
  const stagedAssignmentsByConnectionMap = React.useMemo(() => {
    const map = new Map<string, StagedAssignment[]>();
    stagedAssignments.forEach(s => {
      const list = map.get(s.connection.id) || [];
      list.push(s);
      map.set(s.connection.id, list);
    });
    return map;
  }, [stagedAssignments]);

  // 6. Map of player ID to the staged assignment
  const stagedAssignmentByPlayerMap = React.useMemo(() => {
    const map = new Map<number, StagedAssignment>();
    stagedAssignments.forEach(s => {
      if (!map.has(s.player.id)) {
        map.set(s.player.id, s);
      }
    });
    return map;
  }, [stagedAssignments]);

  // Filter left-side options
  const filteredOptions = React.useMemo(() => {
    const q = optionSearchQuery.toLowerCase();
    return accountOptions.filter(opt => {
      const name = (opt.name || "").toLowerCase();
      const description = (opt.description || "").toLowerCase();
      const matchesSearch = name.includes(q) || description.includes(q);
      if (!matchesSearch) return false;

      if (hideConnectedOptions) {
        const isAlreadyConnected = connectedOptionIdsSet.has(opt.id);
        const isStaged = stagedConnectionIdsSet.has(opt.id);
        if (isAlreadyConnected || isStaged) return false;
      }

      return true;
    });
  }, [accountOptions, optionSearchQuery, hideConnectedOptions, connectedOptionIdsSet, stagedConnectionIdsSet]);

  // Filter right-side players
  const filteredPlayers = React.useMemo(() => {
    const q = playerSearchQuery.toLowerCase();
    return players.filter(p => {
      // 1. Search query filter
      const fName = getPlayerFirstName(p);
      const lName = getPlayerLastName(p);
      const email = getPlayerEmail(p);
      const fullName = `${fName} ${lName}`.toLowerCase();
      const matchesSearch = fullName.includes(q) || email.toLowerCase().includes(q);
      if (!matchesSearch) return false;

      // 2. Segmentation filter
      if (selectedSegmentation) {
        const [type, value] = selectedSegmentation.split(":");
        if (type === "region" && !getPlayerRegions(p).includes(value)) return false;
        if (type === "branch" && !getPlayerBranches(p).includes(value)) return false;
      }

      // 3. "Needs Connection" Filter (hides players who already have a profile linked to this network OR are currently staged)
      if (filterNeedsConnection) {
        const isStaged = stagedPlayerIdsSet.has(p.id);
        const hasConnection = p.activeSocialNetworkProfiles?.some(s => s.socialNetworkId === profile.socialNetworkId);
        if (isStaged || hasConnection) return false;
      }

      return true;
    });
  }, [
    players,
    playerSearchQuery,
    selectedSegmentation,
    filterNeedsConnection,
    stagedPlayerIdsSet,
    profile.socialNetworkId,
  ]);

  // Displayed options (limit rendering to first 100 items + currently selected if outside first 100)
  const displayedOptions = React.useMemo(() => {
    const sliced = filteredOptions.slice(0, 100);
    if (selectedAccountId && !sliced.some(o => o.id === selectedAccountId)) {
      const selectedOpt = filteredOptions.find(o => o.id === selectedAccountId);
      if (selectedOpt) {
        sliced.push(selectedOpt);
      }
    }
    return sliced;
  }, [filteredOptions, selectedAccountId]);

  // Displayed players (limit rendering to first 100 items + currently selected if outside first 100)
  const displayedPlayers = React.useMemo(() => {
    const sliced = filteredPlayers.slice(0, 100);
    if (selectedPlayerId !== null && !sliced.some(p => p.id === selectedPlayerId)) {
      const selectedPly = filteredPlayers.find(p => p.id === selectedPlayerId);
      if (selectedPly) {
        sliced.push(selectedPly);
      }
    }
    return sliced;
  }, [filteredPlayers, selectedPlayerId]);

  // Stage active pair
  const handleStageAssignment = () => {
    const conn = accountOptions.find(o => o.id === selectedAccountId);
    const ply = players.find(p => p.id === selectedPlayerId);

    if (conn && !conn.isDuplicate && ply) {
      setStagedAssignments(prev => {
        // If this connection was already staged, remove the old staged pair first (re-arrange!)
        const filtered = prev.filter(s => s.connection.id !== conn.id);
        return [...filtered, { connection: conn, player: ply }];
      });
      setSelectedAccountId("");
      setSelectedPlayerId(null);
    }
  };

  // Remove staged pair
  const handleUnstageAssignment = (connectionId: string) => {
    setStagedAssignments(prev => prev.filter(s => s.connection.id !== connectionId));
  };

  // Automatically match unlinked players and listings based on player names
  const handleAutoMatch = () => {
    setAutoMatching(true);

    setTimeout(() => {
      // 1. Filter out eligible players (not connected on this network and not staged)
      const eligiblePlayers = players.filter(p => {
        const hasConnection = p.activeSocialNetworkProfiles?.some(s => s.socialNetworkId === profile.socialNetworkId);
        if (hasConnection) return false;
        if (stagedPlayerIdsSet.has(p.id)) return false;

        const fName = getPlayerFirstName(p).trim();
        const lName = getPlayerLastName(p).trim();
        const fullName = `${fName} ${lName}`.trim();
        // Only match if name has at least 5 non-whitespace characters
        if (fullName.replace(/\s+/g, "").length < 5) return false;

        return true;
      });

      // 2. Filter out eligible options (not connected and not staged)
      const eligibleOptions = accountOptions.filter(opt => {
        if (opt.isDuplicate) return false;
        if (connectedOptionIdsSet.has(opt.id)) return false;
        if (stagedConnectionIdsSet.has(opt.id)) return false;
        return true;
      });

      if (eligiblePlayers.length === 0 || eligibleOptions.length === 0) {
        toast.info("No unassigned players or listings available for auto-matching.");
        setAutoMatching(false);
        return;
      }

      // 3. Precompute lowercase search terms for high-speed performance
      const preparedPlayers = eligiblePlayers.map(p => {
        const fName = getPlayerFirstName(p).trim();
        const lName = getPlayerLastName(p).trim();
        const fullName = `${fName} ${lName}`.trim().toLowerCase();
        return { player: p, fullName };
      });

      const preparedOptions = eligibleOptions.map(opt => {
        const optionName = (opt.name || "").trim().toLowerCase();
        return { option: opt, optionName };
      });

      const newStaged: StagedAssignment[] = [];
      const matchedPlayerIds = new Set<number>();
      const matchedOptionIds = new Set<string>();

      // 4. Run substring matching algorithm in O(N + M) precomputed loop
      for (const pInfo of preparedPlayers) {
        if (matchedPlayerIds.has(pInfo.player.id)) continue;

        for (const optInfo of preparedOptions) {
          if (matchedOptionIds.has(optInfo.option.id)) continue;

          if (optInfo.optionName.includes(pInfo.fullName)) {
            newStaged.push({ player: pInfo.player, connection: optInfo.option });
            matchedPlayerIds.add(pInfo.player.id);
            matchedOptionIds.add(optInfo.option.id);
            break; // Match found, move to next player
          }
        }
      }

      if (newStaged.length > 0) {
        setStagedAssignments(prev => {
          // Overwrite any pre-existing staged entries for these matched options/players
          const filtered = prev.filter(
            s => !newStaged.some(n => n.connection.id === s.connection.id || n.player.id === s.player.id)
          );
          return [...filtered, ...newStaged];
        });
        toast.success(`Successfully auto-matched and staged ${newStaged.length} mappings!`);
      } else {
        toast.info("No automatic name-based matches were found.");
      }

      setAutoMatching(false);
    }, 300);
  };

  // Save changes
  const handleBulkAssign = async () => {
    if (!coachId) return;
    if (stagedAssignments.length === 0) return;

    setSubmitting(true);
    setPartialFailure(null);

    const assignments: BulkAssignAssignment[] = stagedAssignments.map(s => ({
      playerId: s.player.id,
      selectedAccountId: s.connection.id,
    }));

    try {
      const response = await CoachSocialNetworkProfileServices.bulkAssign(coachId, profile.id, { assignments });

      if (response.status === 204) {
        toast.success("Successfully bulk-assigned new social connection mappings!");
        onClose();
      } else if (response.status === 207 && response.data) {
        setPartialFailure({
          warning: response.data.warning || "Some connection assignments failed",
          messages: response.data.messages || [],
        });
        toast.warning("Assignments completed with partial failures.");
      }
    } catch (e) {
      console.error(e);
      toast.error("An unexpected error occurred during bulk assignment", {
        description: getErrorMessage(e),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={open => !open && onClose()} size="3xl" className="h-[90vh] max-h-[90vh] flex flex-col">
      <Dialog.Header className="shrink-0">
        <div className="flex items-start justify-between w-full">
          <div className="flex flex-col gap-1">
            <Dialog.Title className="flex items-center gap-2">
              <SocialNetworkIcon socialNetworkId={profile.socialNetworkId} size="mini" />
              Bulk Managed Connections Builder
            </Dialog.Title>
            <Dialog.Description>
              {step === "build"
                ? "Stage connection mapping using the side-by-side builder below."
                : "Review your staged connection mapping list before finalizing."}
            </Dialog.Description>
          </div>
          {step === "build" && !loading && (
            <div className="flex items-center gap-2 shrink-0">
              <Button
                variant="outline"
                size="sm"
                className="font-bold flex items-center gap-1.5"
                onClick={handleAutoMatch}
                disabled={autoMatching}
              >
                {autoMatching ? <Spinner className="size-3.5 mr-1" /> : <Users className="size-3.5 text-primary" />}
                {autoMatching ? "Matching..." : `Auto-Match ${socialNetworkConnectionNames[profile.socialNetworkId]}s`}
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="font-bold flex items-center gap-1.5"
                onClick={() => setShowImportDialog(true)}
              >
                <Plus className="size-3.5 text-primary" />
                Import CSV Mappings
              </Button>
            </div>
          )}
        </div>
      </Dialog.Header>

      <Dialog.Content className="flex-1 overflow-y-auto flex flex-col gap-5 min-h-0">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 gap-4 w-full">
            <Spinner className="size-10 text-primary" />
            <div className="flex flex-col items-center text-center gap-1">
              <span className="font-bold text-sm text-foreground">Preparing your players and connections...</span>
              {loadingPlayersProgress.total > 1 ? (
                <span className="text-xs text-muted-foreground animate-pulse">
                  Fetching player page {loadingPlayersProgress.current} of {loadingPlayersProgress.total}...
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">This may take a moment for large lists.</span>
              )}
            </div>
          </div>
        ) : step === "build" ? (
          <>
            {/* Side-by-Side Builder Layout */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-b border-gray-100 pb-4">
              {/* Left Column: Connections */}
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between pb-1">
                  <span className="font-bold text-xs text-foreground flex items-center gap-1">
                    <CheckIcon className="size-3 text-primary" />
                    1. Select {socialNetworkConnectionNames[profile.socialNetworkId]} ({filteredOptions.length} left)
                  </span>
                  <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-semibold cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={hideConnectedOptions}
                      onChange={e => setHideConnectedOptions(e.target.checked)}
                      className="size-3.5 text-primary rounded border-gray-300 focus:ring-primary"
                    />
                    <span>Hide already connected</span>
                  </label>
                </div>

                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    placeholder={`Search ${socialNetworkConnectionNames[profile.socialNetworkId]}s by name...`}
                    value={optionSearchInputValue}
                    onChange={e => setOptionSearchInputValue(e.target.value)}
                    className="pl-9 h-8 text-sm"
                  />
                </div>

                {filteredOptions.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic text-center py-10 m-0">
                    No unassigned {socialNetworkConnectionNames[profile.socialNetworkId]}s.
                  </p>
                ) : (
                  <>
                    <div className="flex flex-col gap-1 h-[260px] overflow-y-auto border border-gray-100 rounded-lg p-2 bg-gray-50/50">
                      {displayedOptions.map(option => {
                        // Find all players currently connected to this connection option on the server (independently or as children)
                        const matchingPlayers = optionToPlayersMap.get(option.id) || [];

                        // Build the unified list of all players mapped to this connection
                        const mappedPlayersList: { name: string; email: string; isStaged?: boolean }[] = [];

                        // 1. Add newly staged mappings in this session
                        const stagedForOption = stagedAssignmentsByConnectionMap.get(option.id) || [];
                        stagedForOption.forEach(s => {
                          mappedPlayersList.push({
                            name: `${getPlayerFirstName(s.player)} ${getPlayerLastName(s.player)}`,
                            email: getPlayerEmail(s.player),
                            isStaged: true,
                          });
                        });

                        // 2. Add existing initial server links (avoiding duplicate email keys)
                        matchingPlayers.forEach(ply => {
                          const alreadyStaged = mappedPlayersList.some(item => item.email === getPlayerEmail(ply));
                          if (!alreadyStaged) {
                            mappedPlayersList.push({
                              name: `${getPlayerFirstName(ply)} ${getPlayerLastName(ply)}`,
                              email: getPlayerEmail(ply),
                            });
                          }
                        });

                        const hasStaged = mappedPlayersList.some(item => item.isStaged);
                        const hasMultiple = mappedPlayersList.length > 1;
                        const firstMappedName = mappedPlayersList[0]?.name;

                        const warning = option.isDuplicate ? googleDuplicateWarning(option, accountOptions) : undefined;

                        return (
                          <div
                            key={`opt-${option.id}`}
                            className={`flex items-start gap-2 p-1.5 rounded-md transition-colors ${
                              option.isDuplicate
                                ? "opacity-60"
                                : selectedAccountId === option.id
                                ? "bg-primary/10 border-primary"
                                : "hover:bg-gray-100"
                            }`}
                          >
                            <label
                              className={`flex min-w-0 flex-1 items-center gap-2.5 ${
                                option.isDuplicate ? "cursor-not-allowed" : "cursor-pointer"
                              }`}
                            >
                              <input
                                type="radio"
                                name="child-account-builder"
                                checked={selectedAccountId === option.id}
                                disabled={option.isDuplicate}
                                onChange={() => {
                                  if (option.isDuplicate) return;
                                  setSelectedAccountId(option.id);
                                }}
                                className="size-4 text-primary focus:ring-primary border-gray-300 disabled:cursor-not-allowed"
                              />
                              <div className="flex flex-col gap-0.5">
                                <span className="text-xs font-semibold text-foreground">{option.name}</span>
                                {option.description && (
                                  <span className="text-[10px] text-muted-foreground">{option.description}</span>
                                )}
                                {mappedPlayersList.length === 1 && (
                                  <span
                                    className={`text-[10px] font-bold ${hasStaged ? "text-primary" : "text-green-600"}`}
                                  >
                                    {hasStaged ? "Staged Link to: " : "Linked to: "}
                                    {firstMappedName}
                                  </span>
                                )}
                                {hasMultiple && (
                                  <span
                                    onClick={e => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setViewPlayersForOption({ option, players: mappedPlayersList });
                                    }}
                                    title="Click to view all connected players"
                                    className={`text-[10px] font-bold hover:underline cursor-pointer ${
                                      hasStaged ? "text-primary" : "text-green-600"
                                    }`}
                                  >
                                    {hasStaged ? "Staged Link to: " : "Linked to: "}
                                    {firstMappedName} and {mappedPlayersList.length - 1} more...
                                  </span>
                                )}
                              </div>
                            </label>
                            {warning && (
                              <Tooltip>
                                <Tooltip.Trigger
                                  render={
                                    <button
                                      type="button"
                                      className="mt-0.5 shrink-0 text-amber-600 hover:text-amber-700"
                                      aria-label={warning.title}
                                    >
                                      <TriangleAlert className="size-3.5" />
                                    </button>
                                  }
                                />
                                <Tooltip.Content>
                                  <p className="font-medium">{warning.title}</p>
                                  <p>{warning.description}</p>
                                </Tooltip.Content>
                              </Tooltip>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {filteredOptions.length > 100 && (
                      <p className="text-[10px] text-muted-foreground italic mt-1 m-0">
                        Showing first 100 of {filteredOptions.length}{" "}
                        {socialNetworkConnectionNames[profile.socialNetworkId]}s. Use search to narrow down.
                      </p>
                    )}
                  </>
                )}
              </div>

              {/* Right Column: Players */}
              <div className="flex flex-col gap-3 border-l border-gray-100 pl-6">
                <div className="flex items-center justify-between pb-1">
                  <span className="font-bold text-xs text-foreground flex items-center gap-1">
                    <CheckIcon className="size-3 text-primary" />
                    2. Select Player ({filteredPlayers.length} left)
                  </span>
                  <label className="flex items-center gap-1.5 text-[10px] text-muted-foreground font-semibold cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={filterNeedsConnection}
                      onChange={e => setFilterNeedsConnection(e.target.checked)}
                      className="size-3.5 text-primary rounded border-gray-300 focus:ring-primary"
                    />
                    <span>Only show players needing link</span>
                  </label>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                    <Input
                      placeholder="Search players..."
                      value={playerSearchInputValue}
                      onChange={e => setPlayerSearchInputValue(e.target.value)}
                      className="pl-9 h-8 text-sm"
                    />
                  </div>

                  <select
                    value={selectedSegmentation}
                    onChange={e => setSelectedSegmentation(e.target.value)}
                    className="h-8 px-2 rounded-md border border-gray-200 text-xs bg-background text-foreground outline-none focus:ring-1 focus:ring-primary cursor-pointer font-semibold"
                  >
                    <option value="">All Segmentations</option>
                    {uniqueRegions.length > 0 && (
                      <optgroup label="Regions">
                        {uniqueRegions.map(reg => (
                          <option key={`seg-reg-${reg}`} value={`region:${reg}`}>
                            Region: {reg}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {uniqueBranches.length > 0 && (
                      <optgroup label="Branches">
                        {uniqueBranches.map(br => (
                          <option key={`seg-br-${br}`} value={`branch:${br}`}>
                            Branch: {br}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>

                {filteredPlayers.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic text-center py-10 m-0">No players available.</p>
                ) : (
                  <>
                    <div className="flex flex-col gap-1 h-[260px] overflow-y-auto pr-1 border border-gray-100 rounded-lg p-2 bg-gray-50/50">
                      {displayedPlayers.map(p => {
                        const isSelected = selectedPlayerId === p.id;
                        const stagedConn = stagedAssignmentByPlayerMap.get(p.id)?.connection;
                        const linkedAcc = p.activeSocialNetworkProfiles?.find(
                          s => s.socialNetworkId === profile.socialNetworkId
                        );
                        const connectionDisplayName =
                          linkedAcc?.socialIdentity?.displayName || linkedAcc?.socialIdentity?.username;

                        return (
                          <label
                            key={`p-${p.id}`}
                            className={`flex items-center gap-3 p-1.5 rounded-md cursor-pointer transition-colors ${
                              isSelected ? "bg-gray-100" : "hover:bg-gray-50"
                            }`}
                          >
                            <input
                              type="radio"
                              name="player-builder"
                              checked={isSelected}
                              onChange={() => setSelectedPlayerId(p.id)}
                              className="size-4 text-primary focus:ring-primary border-gray-300"
                            />
                            <div className="flex flex-col gap-0.5">
                              <span className="text-xs font-semibold text-foreground">
                                {getPlayerFirstName(p)} {getPlayerLastName(p)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">{getPlayerEmail(p)}</span>
                              {stagedConn && (
                                <span className="text-[10px] text-primary font-bold">
                                  Staged Link to: {stagedConn.name}
                                </span>
                              )}
                              {!stagedConn && connectionDisplayName && (
                                <span className="text-[10px] text-green-600 font-bold">
                                  Linked to: {connectionDisplayName}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {filteredPlayers.length > 100 && (
                      <p className="text-[10px] text-muted-foreground italic mt-1 m-0">
                        Showing first 100 of {filteredPlayers.length} players. Use search to narrow down.
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Mapping Action Row */}
            <div className="flex justify-center py-1">
              <Button
                variant="default"
                size="sm"
                onClick={handleStageAssignment}
                disabled={!selectedAccountId || !selectedPlayerId}
                className="font-bold flex items-center gap-1"
              >
                Stage Connection Mapping ↓
              </Button>
            </div>

            {/* Staged Assignments Table */}
            <div className="flex flex-col gap-2">
              <span className="font-bold text-xs text-foreground">Staged Queue ({stagedAssignments.length})</span>
              {stagedAssignments.length === 0 ? (
                <div className="text-center py-8 border border-dashed border-gray-200 rounded-lg text-xs text-muted-foreground">
                  No connection mappings staged yet. Use the builders to stage them.
                </div>
              ) : (
                <div className="border border-gray-100 rounded-lg h-[160px] overflow-y-auto bg-gray-50/20">
                  <Table className="text-xs">
                    <Table.Header>
                      <Table.Row className="bg-gray-50/80">
                        <Table.Head className="py-1.5 font-bold">Player</Table.Head>
                        <Table.Head className="py-1.5 font-bold">
                          {socialNetworkNames[profile.socialNetworkId]}{" "}
                          {profile.socialNetworkId === "FB" ? "Page" : "Account"}
                        </Table.Head>
                        <Table.Head className="py-1.5 text-right font-bold">Action</Table.Head>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {stagedAssignments.map(st => (
                        <Table.Row key={`staged-${st.connection.id}`} className="hover:bg-gray-50/50">
                          <Table.Cell className="py-1">
                            {getPlayerFirstName(st.player)} {getPlayerLastName(st.player)}
                          </Table.Cell>
                          <Table.Cell className="py-1">
                            <div className="flex flex-col">
                              <span className="font-semibold">{st.connection.name}</span>
                              {st.connection.description && (
                                <span className="text-[10px] text-muted-foreground font-normal">
                                  {st.connection.description}
                                </span>
                              )}
                            </div>
                          </Table.Cell>
                          <Table.Cell className="py-1 text-right">
                            <Button
                              variant="ghost"
                              size="icon-xs"
                              className="text-destructive hover:bg-destructive/10 h-5 w-5"
                              onClick={() => handleUnstageAssignment(st.connection.id)}
                            >
                              <Trash2 className="size-3" />
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Step 2: Review Screen */
          <div className="flex flex-col gap-4">
            <div className="bg-green-50/30 p-3 rounded-lg border border-green-100 flex items-start gap-2.5">
              <Info className="size-4 text-green-700 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-0.5 text-xs text-green-800">
                <span className="font-bold">Final Review Summary</span>
                <span>
                  You are assigning {stagedAssignments.length} unique parent connection listings to your players.
                </span>
              </div>
            </div>

            <div className="border border-gray-100 rounded-lg overflow-hidden">
              <Table className="text-xs">
                <Table.Header className="bg-gray-50">
                  <Table.Row>
                    <Table.Head className="font-bold">Player Name</Table.Head>
                    <Table.Head className="font-bold">Player Email</Table.Head>
                    <Table.Head className="font-bold">
                      Assigned {socialNetworkNames[profile.socialNetworkId]}{" "}
                      {profile.socialNetworkId === "FB" ? "Page" : "Account"}
                    </Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {stagedAssignments.map(s => (
                    <Table.Row key={`summary-${s.connection.id}`} className="hover:bg-gray-50/50">
                      <Table.Cell className="font-semibold">
                        {getPlayerFirstName(s.player)} {getPlayerLastName(s.player)}
                      </Table.Cell>
                      <Table.Cell className="text-muted-foreground">{getPlayerEmail(s.player)}</Table.Cell>
                      <Table.Cell className="font-semibold text-primary">
                        <div className="flex flex-col">
                          <span className="font-semibold text-primary">{s.connection.name}</span>
                          {s.connection.description && (
                            <span className="text-[10px] text-muted-foreground font-normal">
                              {s.connection.description}
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            {/* Partial Failure Alerts (Status Code 207) */}
            {partialFailure && (
              <Alert variant="warning" className="mt-2 flex flex-col gap-2 border-amber-200 bg-amber-50">
                <div className="flex items-center gap-2 text-amber-800 font-semibold text-xs">
                  <AlertCircle className="size-4 text-amber-700 shrink-0" />
                  <span>{partialFailure.warning}</span>
                </div>
                <ul className="text-[10px] text-amber-800 flex flex-col gap-1 list-disc pl-4 mt-1 m-0">
                  {partialFailure.messages.map((msg, i) => (
                    <li key={`fail-msg-${i}`}>{msg}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </div>
        )}
      </Dialog.Content>

      <Dialog.Footer className="shrink-0">
        {step === "build" ? (
          <>
            <Button variant="outline" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              onClick={() => setStep("review")}
              disabled={stagedAssignments.length === 0 || submitting}
              className="font-bold"
            >
              Review {stagedAssignments.length} Assignment{stagedAssignments.length === 1 ? "" : "s"} →
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" onClick={() => setStep("build")} disabled={submitting}>
              ← Back to Builder
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={submitting || stagedAssignments.length === 0}
              className="font-bold"
            >
              {submitting ? (
                <>
                  <Spinner className="size-4 mr-2" /> Saving Assignments...
                </>
              ) : (
                "Confirm & Save All Assignments"
              )}
            </Button>
          </>
        )}
      </Dialog.Footer>

      {/* CSV Import Mappings Dialog */}
      {showImportDialog && (
        <ImportMappingsDialog
          profile={profile}
          players={players}
          accountOptions={accountOptions}
          onClose={() => setShowImportDialog(false)}
          onImportStaged={newStaged => {
            setStagedAssignments(prev => {
              // Deduplicate so that if a connection was already staged, we overwrite it with the CSV one!
              const filtered = prev.filter(s => !newStaged.some(n => n.connection.id === s.connection.id));
              return [...filtered, ...newStaged];
            });
            setShowImportDialog(false);
          }}
        />
      )}

      {/* Connected Players List Dialog (Multi-Assign View) */}
      {viewPlayersForOption && (
        <Dialog open onOpenChange={open => !open && setViewPlayersForOption(null)} size="md" showCloseButton>
          <Dialog.Header>
            <Dialog.Title className="flex items-center gap-2">
              <Users className="size-5 text-primary shrink-0" />
              Connected Players — {viewPlayersForOption.option.name}
            </Dialog.Title>
            <Dialog.Description>
              A complete list of players who are currently connected to this specific social listing option.
            </Dialog.Description>
          </Dialog.Header>

          <Dialog.Content className="max-h-[300px] overflow-y-auto">
            <div className="border border-gray-100 rounded-lg overflow-hidden bg-background">
              <Table className="text-xs">
                <Table.Header className="bg-gray-50/80">
                  <Table.Row>
                    <Table.Head className="font-bold py-1.5 h-7">Player Name</Table.Head>
                    <Table.Head className="font-bold py-1.5 h-7">Player Email</Table.Head>
                  </Table.Row>
                </Table.Header>
                <Table.Body>
                  {viewPlayersForOption.players.map((item, idx) => (
                    <Table.Row key={`linked-player-row-${idx}`} className="h-7 hover:bg-gray-50/30">
                      <Table.Cell className="py-1 font-semibold flex items-center gap-1.5">
                        <span>{item.name}</span>
                        {item.isStaged && (
                          <span className="text-[9px] font-extrabold px-1.5 py-0.5 rounded-full bg-primary/10 text-primary uppercase tracking-wider scale-90">
                            Staged
                          </span>
                        )}
                      </Table.Cell>
                      <Table.Cell className="py-1 text-muted-foreground">{item.email}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </Dialog.Content>

          <Dialog.Footer className="shrink-0">
            <Button onClick={() => setViewPlayersForOption(null)}>Close</Button>
          </Dialog.Footer>
        </Dialog>
      )}
    </Dialog>
  );
}
